import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import type { WmsImportType } from './wms-page-dsl';

// ─── Import Workflow Sheet ─────────────────────────────────────────────────

export function getImportButtonLabel(importType: WmsImportType): string {
  if (importType === 'purchase-orders') return 'Import PO';
  if (importType === 'asn-lines') return 'Import ASN Lines';
  return 'Import SKUs';
}

function getImportTypeLabel(importType: WmsImportType): string {
  if (importType === 'purchase-orders') return 'Purchase Orders';
  if (importType === 'asn-lines') return 'ASN Lines';
  return 'SKUs';
}

function parseCsvText(text: string): Record<string, string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0]!.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuote = false;
    for (const ch of line) {
      if (ch === '"') { inQuote = !inQuote; continue; }
      if (ch === ',' && !inQuote) { values.push(current.trim()); current = ''; continue; }
      current += ch;
    }
    values.push(current.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
    return row;
  });
}

export function WmsImportWorkflowSheet({
  open, onClose, onSuccess, initialImportType, initialAttachmentId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (importType: WmsImportType) => void;
  initialImportType?: WmsImportType;
  initialAttachmentId?: string;
}) {
  type PoPreviewLine = {
    sourceLineKey: string;
    lineNumber: number;
    skuCode: string | null;
    skuName: string | null;
    incomingQty: number;
    existingQtyOrdered: number;
    existingQtyFulfilled: number;
    changeType: 'NEW' | 'UPDATED' | 'UNCHANGED' | 'REMOVED';
    exceptionStatus: string;
    exceptionNote: string | null;
  };

  type PoPreviewItem = {
    fileName?: string | null;
    poNumber: string;
    supplierName: string | null;
    projectCode: string | null;
    currentProjectCode: string | null;
    orderId: string | null;
    orderExists: boolean;
    currentVersion: number;
    nextVersion: number;
    rowsTotal: number;
    summary: {
      added: number;
      updated: number;
      unchanged: number;
      removed: number;
      exceptions: number;
    };
    lines: PoPreviewLine[];
  };

  type UploadedCsvFile = {
    fileName: string;
    csvText?: string;
    attachmentId?: string;
    previewRows: Record<string, string>[];
  };

  const [importType, setImportType] = useState<WmsImportType>('skus');
  const [warehouseId, setWarehouseId] = useState('');
  const [asnId, setAsnId] = useState('');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: string; project_code: string; project_name?: string | null }>>([]);
  const [importSource, setImportSource] = useState<'upload' | 'shared'>('upload');
  const [sharedDocs, setSharedDocs] = useState<Array<{ id: string; filename: string; document_type?: string | null; created_at?: string | null; notes?: string | null }>>([]);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFiles, setCsvFiles] = useState<UploadedCsvFile[]>([]);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState<{ imported: number; errors: Array<{ row: number; error: string }> } | null>(null);
  const [poPreview, setPoPreview] = useState<{ affectedPoCount: number; items: PoPreviewItem[]; errors: Array<{ row: number; error: string }> } | null>(null);
  const [previewTab, setPreviewTab] = useState<'raw' | 'parsed'>('raw');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setImportType(initialImportType ?? 'skus');
    setImportSource(initialImportType === 'purchase-orders' ? 'upload' : initialAttachmentId ? 'shared' : 'upload');
    setSelectedAttachmentId(initialAttachmentId ?? '');
    setProjectId('');
    setPreview([]);
    setFileName('');
    setCsvText('');
    setCsvFiles([]);
    setResult(null);
    setPoPreview(null);
    setPreviewTab('raw');
    setError('');
    api.get<any>('/wms/warehouses').then(res => {
      const rawWarehouses = Array.isArray(res) ? res : res?.items ?? [];
      const warehouseItems = rawWarehouses.map((w: any) => ({ id: w.id, name: w.name }));
      setWarehouses(warehouseItems);
      setWarehouseId((current) => current || warehouseItems[0]?.id || '');
    }).catch(() => {});
    api.get<any>('/wms/projects').then(res => {
      setProjects(Array.isArray(res) ? res : []);
    }).catch(() => setProjects([]));
    api.get<any>(`/wms/attachments?referenceType=WMS_FILE_STORE&referenceId=${encodeURIComponent('00000000-0000-4000-a000-00000000f001')}&documentType=PO`)
      .then((res) => {
        setSharedDocs(Array.isArray(res?.items) ? res.items : []);
      })
      .catch(() => setSharedDocs([]));
  }, [initialAttachmentId, initialImportType, open]);

  const selectedProjectCode = projects.find((project) => project.id === projectId)?.project_code;
  const isPurchaseOrderImport = importType === 'purchase-orders';
  const lockedImportType = Boolean(initialImportType);
  const lockedPurchaseOrderImport = initialImportType === 'purchase-orders';
  const effectiveWarehouseId = isPurchaseOrderImport ? (warehouseId || warehouses[0]?.id || '') : warehouseId;

  useEffect(() => {
    setPoPreview(null);
    setResult(null);
    setError('');
  }, [importType, warehouseId, asnId, importSource, selectedAttachmentId]);

  const isCsvPoUpload = (file: File) => {
    const lowerName = file.name.toLowerCase();
    return lowerName.endsWith('.csv') || lowerName.endsWith('.txt') || file.type.includes('csv') || file.type.startsWith('text/');
  };

  const uploadPoAttachment = async (file: File) => {
    if (!effectiveWarehouseId) throw new Error('No warehouse is configured for PO import');
    const form = new FormData();
    form.append('file', file);
    form.append('warehouseId', effectiveWarehouseId);
    form.append('referenceType', 'WMS_FILE_STORE');
    form.append('referenceId', '00000000-0000-4000-a000-00000000f001');
    form.append('documentType', 'PO');
    form.append('notes', 'PO import upload');
    return api.post<any>('/wms/attachments', form);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    try {
      const loadedFiles = await Promise.all(files.map(async (file) => {
        if (importType === 'purchase-orders' && !isCsvPoUpload(file)) {
          const attachment = await uploadPoAttachment(file);
          return {
            fileName: file.name,
            attachmentId: attachment?.id,
            previewRows: [],
          };
        }
        const text = await file.text();
        return {
          fileName: file.name,
          csvText: text,
          previewRows: parseCsvText(text).slice(0, 100),
        };
      }));
      const primaryFile = loadedFiles[0]!;
      setFileName(importType === 'purchase-orders'
        ? loadedFiles.map((file) => file.fileName).join(', ')
        : primaryFile.fileName);
      setCsvText(primaryFile.csvText ?? '');
      setCsvFiles(importType === 'purchase-orders' ? loadedFiles : [primaryFile]);
      setPreview(importType === 'purchase-orders'
        ? loadedFiles.flatMap((file) => file.previewRows.map((row) => ({ __file: file.fileName, ...row }))).slice(0, 100)
        : primaryFile.previewRows);
      setResult(null);
      setPoPreview(null);
      setError('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to read import file');
    }
  };

  const loadPoPreview = useCallback(async () => {
    if (importType !== 'purchase-orders') return;
    if (!effectiveWarehouseId) { setError('No warehouse is configured for PO import'); return; }
    if (importSource === 'upload' && csvFiles.length === 0) { setError('Upload a CSV file first'); return; }
    if (importSource === 'shared' && !selectedAttachmentId) { setError('Select a shared PO document first'); return; }

    const previewRequests = importSource === 'upload'
      ? csvFiles.map((file) => ({
        attachmentId: file.attachmentId,
        fileName: file.fileName,
        csvText: file.attachmentId ? undefined : file.csvText,
        rows: file.attachmentId ? undefined : file.previewRows,
      }))
      : [{
        attachmentId: selectedAttachmentId,
        fileName: undefined,
        csvText: undefined,
        rows: undefined,
      }];
    const previewResults = await Promise.all(previewRequests.map(async (requestPayload) => {
      const res = await api.post<any>('/wms/import/preview', {
        type: 'purchase-orders',
        warehouseId: effectiveWarehouseId,
        projectId: lockedPurchaseOrderImport ? undefined : projectId || undefined,
        projectCode: lockedPurchaseOrderImport ? undefined : selectedProjectCode || undefined,
        attachmentId: requestPayload.attachmentId,
        fileName: requestPayload.fileName,
        csvText: requestPayload.csvText,
        rows: requestPayload.rows,
      });
      return {
        fileName: requestPayload.fileName ?? res?.fileName ?? null,
        affectedPoCount: Number(res?.affectedPoCount ?? 0),
        items: Array.isArray(res?.items) ? res.items : [],
        errors: Array.isArray(res?.errors) ? res.errors : [],
      };
    }));
    const nextPreview = {
      affectedPoCount: previewResults.reduce((sum, item) => sum + item.affectedPoCount, 0),
      items: previewResults.flatMap((resultItem) => resultItem.items.map((item: PoPreviewItem) => ({
        ...item,
        fileName: item.fileName ?? resultItem.fileName,
      }))),
      errors: previewResults.flatMap((resultItem) => resultItem.errors.map((previewError: { row: number; error: string }) => ({
        row: previewError.row,
        error: resultItem.fileName ? `${resultItem.fileName}: ${previewError.error}` : previewError.error,
      }))),
    };
    setPoPreview(nextPreview);
    return nextPreview;
  }, [
    csvFiles,
    effectiveWarehouseId,
    importSource,
    importType,
    lockedPurchaseOrderImport,
    projectId,
    selectedAttachmentId,
    selectedProjectCode,
  ]);

  useEffect(() => {
    if (importType !== 'purchase-orders') return;
    const sourceReady = importSource === 'upload' ? csvFiles.length > 0 : Boolean(selectedAttachmentId);
    if (!sourceReady || !effectiveWarehouseId) return;
    let active = true;
    setPreviewing(true);
    setError('');
    setResult(null);
    void loadPoPreview()
      .catch((err: any) => {
        if (active) setError(err?.message ?? 'Preview failed');
      })
      .finally(() => {
        if (active) setPreviewing(false);
      });
    return () => {
      active = false;
    };
  }, [csvFiles, effectiveWarehouseId, importSource, importType, loadPoPreview, selectedAttachmentId]);

  const handleImport = async () => {
    if (!effectiveWarehouseId) { setError(isPurchaseOrderImport ? 'No warehouse is configured for PO import' : 'Select a warehouse'); return; }
    if (importType !== 'purchase-orders' && preview.length === 0) { setError('No data to import'); return; }
    if (importType === 'asn-lines' && !asnId) { setError('ASN ID is required for ASN lines import'); return; }
    if (importType === 'purchase-orders' && importSource === 'upload' && csvFiles.length === 0) { setError('Upload a CSV file first'); return; }
    if (importType === 'purchase-orders' && importSource === 'shared' && !selectedAttachmentId) { setError('Select a shared PO document first'); return; }

    setImporting(true);
    setError('');
    try {
      if (importType === 'purchase-orders' && !poPreview) {
        await loadPoPreview();
      }
      const res = importType === 'purchase-orders' && importSource === 'upload'
        ? await csvFiles.reduce(async (previous, file) => {
          const aggregate = await previous;
          const singleResult = await api.post<any>('/wms/import', {
            type: 'purchase-orders',
            warehouseId: effectiveWarehouseId,
            projectId: lockedPurchaseOrderImport ? undefined : projectId || undefined,
            projectCode: lockedPurchaseOrderImport ? undefined : selectedProjectCode || undefined,
            fileName: file.fileName,
            attachmentId: file.attachmentId,
            csvText: file.attachmentId ? undefined : file.csvText,
            rows: file.attachmentId ? undefined : file.previewRows,
          });
          return {
            imported: aggregate.imported + Number(singleResult?.imported ?? 0),
            errors: [
              ...aggregate.errors,
              ...(Array.isArray(singleResult?.errors) ? singleResult.errors.map((importError: { row: number; error: string }) => ({
                row: importError.row,
                error: `${file.fileName}: ${importError.error}`,
              })) : []),
            ],
          };
        }, Promise.resolve({ imported: 0, errors: [] as Array<{ row: number; error: string }> }))
        : await api.post<any>('/wms/import', {
          type: importType,
          warehouseId: effectiveWarehouseId,
          asnId: importType === 'asn-lines' ? asnId : undefined,
          projectId: importType === 'purchase-orders' && !lockedPurchaseOrderImport ? projectId || undefined : undefined,
          projectCode: importType === 'purchase-orders' && !lockedPurchaseOrderImport ? selectedProjectCode || undefined : undefined,
          attachmentId: importType === 'purchase-orders' && importSource === 'shared' ? selectedAttachmentId : undefined,
          fileName: importSource === 'upload' ? fileName || undefined : undefined,
          csvText: importSource === 'upload' ? csvText || undefined : undefined,
          rows: importType === 'purchase-orders' && importSource === 'shared' ? undefined : preview,
        });
      setResult(res);
      if (res?.imported > 0) {
        setTimeout(() => { onSuccess(importType); }, importType === 'purchase-orders' ? 600 : 1500);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const previewHeaders = preview.length > 0 ? Object.keys(preview[0]!) : [];
  const importDisabled = importing
    || !effectiveWarehouseId
    || (importType === 'purchase-orders'
      ? (importSource === 'upload' ? csvFiles.length === 0 : !selectedAttachmentId)
      : preview.length === 0);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent dock="right" className="flex h-[min(92vh,980px)] w-[min(100vw-32px,1280px)] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 pr-12">
          <DialogTitle>{lockedPurchaseOrderImport ? 'Import Purchase Order' : isPurchaseOrderImport ? 'PO Bulk Import' : 'CSV Bulk Import'}</DialogTitle>
          <DialogDescription className="text-xs">
            {lockedPurchaseOrderImport ? 'Upload a PO file, preview the version diff, then import.' : isPurchaseOrderImport ? 'Upload CSV or Excel purchase order files.' : 'Upload a CSV file to import SKUs or ASN lines.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-4">
          {error && (
            <div className="flex shrink-0 items-center gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          {result && (
            <div className={`shrink-0 rounded border p-2 text-sm ${result.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
              <p><strong>{result.imported}</strong> rows imported successfully.</p>
              {result.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-red-600">{result.errors.length} errors</summary>
                  <ul className="text-xs mt-1 space-y-0.5">
                    {result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
          {poPreview && importType === 'purchase-orders' && (
            <div className={`shrink-0 rounded border p-2 text-sm ${poPreview.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'}`}>
              <p><strong>{poPreview.affectedPoCount}</strong> purchase order(s) affected in preview.</p>
              {poPreview.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-red-600">{poPreview.errors.length} preview errors</summary>
                  <ul className="text-xs mt-1 space-y-0.5">
                    {poPreview.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="grid shrink-0 grid-cols-1 gap-3 rounded border bg-muted/10 p-3 md:grid-cols-12">
            <div className={importType === 'purchase-orders' && importSource === 'upload' ? 'md:col-span-3' : 'md:col-span-4'}>
              <Label className="text-xs">Import Type</Label>
              {lockedImportType ? (
                <div className="mt-1 flex h-8 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">
                  {getImportTypeLabel(importType)}
                </div>
              ) : (
                <Select value={importType} onValueChange={v => setImportType(v as WmsImportType)}>
                  <SelectTrigger className="h-8 mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skus">SKUs</SelectItem>
                    <SelectItem value="asn-lines">ASN Lines</SelectItem>
                    <SelectItem value="purchase-orders">Purchase Orders</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {!isPurchaseOrderImport && (
            <div className="md:col-span-4">
              <Label className="text-xs">Warehouse</Label>
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger className="h-8 mt-1 text-sm"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}

            {importType === 'asn-lines' && (
            <div className="md:col-span-4">
              <Label className="text-xs">ASN ID</Label>
              <Input className="h-8 mt-1 text-sm" placeholder="Enter ASN UUID" value={asnId} onChange={e => setAsnId(e.target.value)} />
            </div>
            )}

            {importType === 'purchase-orders' && !lockedPurchaseOrderImport && (
            <div className="md:col-span-3">
              <Label className="text-xs">PO Source</Label>
              <Select value={importSource} onValueChange={v => setImportSource(v as 'upload' | 'shared')}>
                <SelectTrigger className="h-8 mt-1 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload">Upload New File</SelectItem>
                  <SelectItem value="shared">Pick From Shared Documents</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {importType === 'purchase-orders' && !lockedPurchaseOrderImport && (
            <div className="md:col-span-3">
              <Label className="text-xs">Project Fallback</Label>
              <Select value={projectId || '__none'} onValueChange={(value) => setProjectId(value === '__none' ? '' : value)}>
                <SelectTrigger className="h-8 mt-1 text-sm">
                  <SelectValue placeholder="Optional project for imported PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No project fallback</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_code}{project.project_name ? ` • ${project.project_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {(importType !== 'purchase-orders' || importSource === 'upload') && (
            <div className={importType === 'purchase-orders' ? 'md:col-span-6' : 'md:col-span-4'}>
              <Label className="text-xs">{importType === 'purchase-orders' ? 'PO File(s)' : 'CSV File'}</Label>
              <div className="mt-1">
                <Input
                  ref={fileRef}
                  type="file"
                  accept={importType === 'purchase-orders' ? '.csv,.txt,.xlsx,.xls,.xlsm,.pdf,.png,.jpg,.jpeg,.webp' : '.csv,.txt'}
                  multiple={importType === 'purchase-orders'}
                  className="h-8 text-sm"
                  onChange={handleFileChange}
                />
              </div>
              {importType === 'purchase-orders' && csvFiles.length > 0 && (
                <div className="mt-2 max-h-20 overflow-y-auto rounded border bg-muted/30 p-2 text-xs">
                  <div className="font-medium">{csvFiles.length} file(s) selected</div>
                  <ul className="mt-1 space-y-0.5 text-muted-foreground">
                    {csvFiles.map((file) => (
                      <li key={file.fileName}>{file.fileName} • {file.attachmentId ? 'server parsed' : `${file.previewRows.length} preview row(s)`}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-1 truncate text-xs text-muted-foreground" title={
                importType === 'skus'
                  ? 'Headers: skuCode, name, category, uom, weight, volume'
                  : importType === 'asn-lines'
                    ? 'Headers: skuCode, expectedQty, uom, lotNumber'
                    : 'Select one or more PO CSV or Excel files. Each file is imported separately and kept in PO Import History with its own traceability.'
              }>
                {importType === 'skus'
                  ? 'Headers: skuCode, name, category, uom, weight, volume'
                  : importType === 'asn-lines'
                    ? 'Headers: skuCode, expectedQty, uom, lotNumber'
                    : 'Select one or more PO CSV or Excel files. Each file is imported separately and kept in PO Import History with its own traceability.'
                }
              </p>
            </div>
            )}

            {importType === 'purchase-orders' && importSource === 'shared' && !lockedPurchaseOrderImport && (
            <div className="space-y-2 md:col-span-6">
              <Label className="text-xs">Shared PO Document</Label>
              <Select value={selectedAttachmentId} onValueChange={setSelectedAttachmentId}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select a PO document from Shared Documents" />
                </SelectTrigger>
                <SelectContent>
                  {sharedDocs.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.filename}{doc.created_at ? ` • ${new Date(doc.created_at).toLocaleDateString()}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAttachmentId && (
                <div className="rounded border bg-muted/30 p-2 text-xs text-muted-foreground">
                  {sharedDocs.find((doc) => doc.id === selectedAttachmentId)?.notes || 'No notes'}
                </div>
              )}
            </div>
            )}
          </div>

          {importType === 'purchase-orders' && ((importSource === 'upload' && preview.length > 0) || poPreview) ? (
            <Tabs
              value={previewTab}
              onValueChange={(value) => setPreviewTab(value as 'raw' | 'parsed')}
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
            >
              <TabsList className="w-full justify-start overflow-x-auto rounded-md border bg-background p-1">
                <TabsTrigger value="raw" className="text-xs">
                  Raw Preview
                  {preview.length > 0 ? <span className="ml-1 text-[11px] text-muted-foreground">({preview.length})</span> : null}
                </TabsTrigger>
                <TabsTrigger value="parsed" className="text-xs">
                  Parsed View
                  {poPreview ? <span className="ml-1 text-[11px] text-muted-foreground">({poPreview.items.length})</span> : null}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="raw" className="mt-2 min-h-0 min-w-0 flex-1 overflow-hidden">
                {preview.length > 0 ? (
                  <div className="flex min-h-0 h-full flex-col">
                    <div className="mb-1 flex shrink-0 items-center justify-between gap-2">
                      <Label className="text-xs">Raw Preview ({preview.length} rows)</Label>
                      <span className="text-[11px] text-muted-foreground">Scroll horizontally and vertically to inspect columns and rows.</span>
                    </div>
                    <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 min-w-0 flex-1 overflow-scroll rounded border">
                      <table className="min-w-max border-separate border-spacing-0 text-xs leading-5">
                        <thead className="sticky top-0 z-10">
                          <tr className="border-b bg-muted">
                            {previewHeaders.map(h => <th key={h} className="min-w-[160px] whitespace-nowrap px-2 py-1.5 text-left font-medium">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.map((row, i) => (
                            <tr key={i} className="border-b">
                              {previewHeaders.map(h => (
                                <td
                                  key={h}
                                  className="min-w-[160px] max-w-[360px] align-top whitespace-pre-wrap break-words px-2 py-1.5"
                                  title={row[h]}
                                >
                                  {row[h]}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full min-h-[240px] items-center justify-center rounded border border-dashed text-sm text-muted-foreground">
                    No raw preview available.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="parsed" className="mt-2 min-h-0 min-w-0 flex-1 overflow-hidden">
                {poPreview ? (
                  <div className="flex min-h-0 h-full flex-col space-y-2">
                    <Label className="shrink-0 text-xs">Parsed PO Preview</Label>
                    <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 min-w-0 flex-1 overflow-scroll rounded border p-3">
                      <div className="min-w-max space-y-3">
                        {poPreview.items.map((item) => (
                          <details key={item.poNumber} className="rounded border p-3" open>
                            <summary className="cursor-pointer list-none">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <div className="text-sm font-medium">
                                    {item.poNumber} {item.supplierName ? `• ${item.supplierName}` : ''}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {item.orderExists ? `V${item.currentVersion} -> V${item.nextVersion}` : 'New PO'} • {item.rowsTotal} incoming row(s)
                                  </div>
                                  {item.fileName ? (
                                    <div className="text-xs text-muted-foreground">File: {item.fileName}</div>
                                  ) : null}
                                  <div className="text-xs text-muted-foreground">
                                    Project: {item.currentProjectCode && item.projectCode && item.currentProjectCode !== item.projectCode
                                      ? `${item.currentProjectCode} -> ${item.projectCode}`
                                      : item.projectCode ?? item.currentProjectCode ?? '-'}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="rounded bg-green-50 px-2 py-1 text-green-700">+{item.summary.added}</span>
                                  <span className="rounded bg-blue-50 px-2 py-1 text-blue-700">~{item.summary.updated}</span>
                                  <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">={item.summary.unchanged}</span>
                                  <span className="rounded bg-amber-50 px-2 py-1 text-amber-700">-{item.summary.removed}</span>
                                  <span className="rounded bg-red-50 px-2 py-1 text-red-700">!{item.summary.exceptions}</span>
                                </div>
                              </div>
                            </summary>
                            <div className="mt-3 runtime-grid-scrollbar boox-x-scroll max-h-[32vh] overflow-scroll">
                              <table className="min-w-[900px] border-separate border-spacing-0 text-xs">
                                <thead className="sticky top-0 z-10">
                                  <tr className="border-b bg-muted">
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Line</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">SKU</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Incoming</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Current</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Received</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Change</th>
                                    <th className="whitespace-nowrap px-2 py-1 text-left">Exception</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.lines.map((line) => (
                                    <tr key={`${item.poNumber}-${line.sourceLineKey}`} className="border-b">
                                      <td className="whitespace-nowrap px-2 py-1">{line.lineNumber}</td>
                                      <td className="whitespace-nowrap px-2 py-1">
                                        <div>{line.skuCode || '-'}</div>
                                        {line.skuName ? <div className="text-muted-foreground">{line.skuName}</div> : null}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-1">{line.incomingQty}</td>
                                      <td className="whitespace-nowrap px-2 py-1">{line.existingQtyOrdered}</td>
                                      <td className="whitespace-nowrap px-2 py-1">{line.existingQtyFulfilled}</td>
                                      <td className="whitespace-nowrap px-2 py-1">{line.changeType}</td>
                                      <td className="whitespace-nowrap px-2 py-1">
                                        <div>{line.exceptionStatus}</div>
                                        {line.exceptionNote ? <div className="text-muted-foreground">{line.exceptionNote}</div> : null}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full min-h-[240px] items-center justify-center rounded border border-dashed text-sm text-muted-foreground">
                    Parsed preview will appear after file parsing.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : preview.length > 0 && (importType !== 'purchase-orders' || importSource === 'upload') && (
            <div className={`flex min-h-0 flex-col ${importType === 'purchase-orders' ? 'shrink-0 basis-[34%]' : 'flex-1'}`}>
              <div className="mb-1 flex shrink-0 items-center justify-between gap-2">
                <Label className="text-xs">Raw Preview ({preview.length} rows)</Label>
                <span className="text-[11px] text-muted-foreground">Scroll horizontally and vertically to inspect columns and rows.</span>
              </div>
              <div className={`runtime-grid-scrollbar boox-x-scroll overflow-auto rounded border ${importType === 'purchase-orders' ? 'min-h-[220px] max-h-[34vh]' : 'min-h-[280px] flex-1'}`}>
                <table className="min-w-max border-separate border-spacing-0 text-xs leading-5">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b bg-muted">
                      {previewHeaders.map(h => <th key={h} className="min-w-[160px] whitespace-nowrap px-2 py-1.5 text-left font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-b">
                        {previewHeaders.map(h => (
                          <td
                            key={h}
                            className="min-w-[160px] max-w-[360px] align-top whitespace-pre-wrap break-words px-2 py-1.5"
                            title={row[h]}
                          >
                            {row[h]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>

        <DialogFooter className="shrink-0 border-t bg-background px-6 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={importing || previewing}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={importDisabled}
          >
            {importing && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {importType === 'purchase-orders' ? 'Import PO' : `Import ${preview.length} Rows`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
